<template>
  <div class="cp" v-if="visible" :class="{ 'cp--window': windowMode }">
    <!-- ── #2091 r4 — undocked: the chat lives in another window, so the main
         page shows only a way to bring it back and otherwise gets its full
         width (no panel, no reserved gutter, left rail restored). ─────────── -->
    <div v-if="!windowMode && undocked" class="cp__away" data-testid="chat-undocked-pill">
      <i class="fa fa-external-link" aria-hidden="true"></i>
      <span>Chat is open in another window</span>
      <button type="button" class="cp__awayb" data-testid="chat-dock" @click="dock()">Dock</button>
    </div>

    <!-- ── the docked launcher, when the panel is closed ─────────────────── -->
    <button v-if="!windowMode && !undocked && collapsed" type="button" class="cp__rail" data-testid="chat-launcher"
            @click="collapsed = false">
      <i class="fa fa-comment" aria-hidden="true"></i>
      <span>Chat</span>
      <span v-if="unreadTotal" class="cp__railn">{{ unreadTotal }}</span>
    </button>

    <!-- ── the 60-second toast ──────────────────────────────────────────── -->
    <div v-if="toast && !windowMode && !undocked" class="cp__toast" role="status" aria-live="polite" data-testid="chat-toast">
      <div class="cp__toasth">
        <span>New message in</span>
        <span class="cp__toastw">#{{ toast.where }}</span>
        <span class="cp__toastcd">{{ toastLeft }} s</span>
      </div>
      <div class="cp__toastb">
        <span class="cp__av" :style="{ background: toast.who.c }">{{ toast.who.i }}</span>
        <div>
          <div class="cp__toastn">{{ toast.who.n }}</div>
          <div class="cp__toastx">{{ toast.body }}</div>
        </div>
      </div>
      <div class="cp__toasta">
        <button type="button" class="cp__btn cp__btn--p" @click="openFromToast()">Open chat</button>
        <button type="button" class="cp__btn" @click="hideToast()">Dismiss</button>
      </div>
      <div class="cp__bar"><i :style="{ width: (toastLeft / 60 * 100) + '%' }"></i></div>
    </div>

    <!-- ── the panel ────────────────────────────────────────────────────── -->
    <aside v-if="windowMode || (!undocked && !collapsed)" class="cp__panel" aria-label="Chat" data-testid="chat-panel">
      <div class="cp__head">
        <button v-if="view === 'channel'" type="button" class="cp__ico" title="Back to channels"
                aria-label="Back to channels" @click="view = 'list'">
          <i class="fa fa-chevron-left" aria-hidden="true"></i>
        </button>
        <div class="cp__title">
          <span class="cp__t1">{{ headTitle }}</span>
          <span class="cp__t2">{{ headSub }}</span>
        </div>
        <button v-if="mayAdmin && !windowMode" type="button" class="cp__ico" title="Chat settings"
                aria-label="Chat settings" data-testid="chat-gear" @click="openSettings()">
          <i class="fa fa-cog" aria-hidden="true"></i>
        </button>
        <!-- #2091 r4 — undock in the docked panel, dock in the pop-out. One
             control, two directions, so there is never a window with no way
             back. -->
        <button v-if="!windowMode" type="button" class="cp__ico" title="Open chat in its own window"
                aria-label="Open chat in its own window" data-testid="chat-undock" @click="undock()">
          <i class="fa fa-external-link" aria-hidden="true"></i>
        </button>
        <button v-else type="button" class="cp__ico" title="Dock chat back into the page"
                aria-label="Dock chat back into the page" data-testid="chat-redock" @click="dockFromWindow()">
          <i class="fa fa-compress" aria-hidden="true"></i>
        </button>
        <button v-if="!windowMode" type="button" class="cp__ico" title="Collapse chat" aria-label="Collapse chat"
                @click="collapsed = true">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>

      <!-- #2091 r5 — VIEW AS. A preview-only strip, and it says so.
           Ed asked to see the EMPLOYER outside-hours state. The panel otherwise
           takes its chat role from the signed-in CS3 role, so a reviewer signed
           in as a Super Admin is `cs_admin` — and CS staff are never restricted
           by a client's schedule, which means the read-only state is invisible
           to the very person reviewing it. Named people are listed as well as
           roles because "person beats role beats schedule" cannot be shown by
           picking a role alone. -->
      <!-- ⚠ #2096 — GONE IN A LIVE BUILD. Looking through another role's eyes
           is meaningful against invented data and dangerous against real data:
           the channel list and the composer would claim a role the server did
           not grant, so the panel would show a state the user cannot actually
           be in, and every post would still go out as themselves. -->
      <div v-if="!isLive" class="cp__viewas" data-testid="chat-viewas">
        <label class="cp__vl" for="cp-viewas">Preview as</label>
        <select id="cp-viewas" class="cp__vs" v-model="viewAs" data-testid="chat-viewas-select">
          <optgroup label="Role">
            <option v-for="r in previewRoles" :key="'r:' + r" :value="'r:' + r">
              {{ rolePlural[r] || roleLabel[r] || r }}
            </option>
          </optgroup>
          <optgroup v-if="namedPeople.length" label="Named individually">
            <option v-for="x in namedPeople" :key="'p:' + x.eid" :value="'p:' + x.eid">
              {{ x.name }} · {{ roleLabel[x.role] || x.role }}
            </option>
          </optgroup>
        </select>
        <span class="cp__vn">{{ viewAsNote }}</span>
      </div>

      <div v-if="popupBlocked" class="cp__blocked" data-testid="chat-popup-blocked">
        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
        <span>
          Your browser blocked the chat window, so chat stayed here. Allow pop-ups for this site and
          try Undock again.
        </span>
      </div>

      <div class="cp__body" ref="body">
        <!-- channel list -->
        <template v-if="view === 'list'">
          <div v-for="g in groupsShown" :key="g.key" class="cp__grp">
            <div class="cp__grph">
              <span>{{ g.title }}</span>
              <span v-if="g.staffOnly" class="cp__staff">staff only</span>
            </div>
            <button v-for="c in g.rows" :key="c.id" type="button"
                    class="cp__ch" :class="{ 'cp__unread': c.unread, 'cp__on': c.id === openId }"
                    @click="openChannel(c.id)">
              <span class="cp__hash">#</span>
              <span class="cp__chmain">
                <span class="cp__chn">
                  {{ c.label }}
                  <span v-if="c.archived" class="cp__arch">archived</span>
                </span>
                <span class="cp__chs">{{ c.last }}</span>
              </span>
              <span class="cp__chr">
                <span class="cp__cht">{{ c.at }}</span>
                <span v-if="c.unread" class="cp__pill">{{ c.unread }}</span>
              </span>
            </button>
          </div>

          <div v-if="role === 'member'" class="cp__note">
            <strong>No global channels.</strong> A Member can never be granted access to a
            CS-world channel. That is a rule, not a setting a CA can change.
          </div>
          <div v-else-if="role === 'steward' && !granted" class="cp__note">
            <strong>No global channels yet.</strong> Stewards and Power Users see CS-world
            channels only once their own Client Admin grants it.
          </div>
        </template>

        <!-- a channel -->
        <template v-else>
          <div v-if="atCap" class="cp__cap">
            <strong>That’s as far back as chat goes.</strong>
            The panel holds the most recent 120 messages. Older messages are available in
            <span class="cp__where">Reports</span> — a Client Admin or Power User can pull them
            for any channel in this client.
          </div>
          <div v-else class="cp__older">
            <button type="button" class="cp__olderb" @click="loadOlder()">Load older messages</button>
            <div class="cp__olderc">showing {{ shownCount }} of 120 the panel can reach</div>
          </div>

          <template v-for="(m, i) in slice">
            <div v-if="m.day !== dayOf(i - 1)" :key="'d' + i" class="cp__day">{{ m.day }}</div>
            <div :key="'m' + i" class="cp__msg" :class="{ 'cp__cont': isCont(i) }">
              <span class="cp__av" :class="{ 'cp__avsp': isCont(i) }"
                    :style="{ background: isCont(i) ? 'transparent' : m.who.c }">{{ isCont(i) ? '' : m.who.i }}</span>
              <div class="cp__mmain">
                <div v-if="!isCont(i)" class="cp__mh">
                  <span class="cp__mn">{{ m.who.n }}</span>
                  <span class="cp__mrole" :class="{ 'cp__staffrole': m.staff }">{{ roleLabel[m.who.r] || m.who.r }}</span>
                  <span class="cp__mt">{{ m.at }}</span>
                </div>
                <!-- Plain text, always: a message must never be interpreted as markup. -->
                <div class="cp__mb">{{ m.body }}</div>
                <div v-if="m.mailtoNote" class="cp__sani">
                  the address was left as text — a user@host link is a common phishing disguise,
                  so it is never linked
                </div>
                <div v-if="m.link" class="cp__lp">
                  <span class="cp__lpic">{{ m.link.letter }}</span>
                  <div class="cp__lpm">
                    <div class="cp__lpt">{{ m.link.title }}</div>
                    <div class="cp__lph">{{ m.link.host }}</div>
                    <div v-if="m.link.punycode" class="cp__lpw">⚠ {{ m.link.punycode }}</div>
                    <div v-if="m.link.svgRefused" class="cp__sani">
                      the site’s icon was an SVG, which is refused because it can carry script —
                      initial shown instead
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>

      <!-- composer, or why there isn't one -->
      <div v-if="view === 'channel'">
        <div v-if="channel.archived" class="cp__ro" data-testid="chat-archived">
          <i class="fa fa-archive" aria-hidden="true"></i>
          <div>
            <strong>This channel is archived</strong>
            It is read-only for everyone, including Client Admins and CallSteward staff. The
            conversation stays readable here until it ages out, and in Reports after that.
          </div>
        </div>
        <!-- #2091 r3 — a job channel outside its window. Its own banner, not the
             office-hours one: the reason is different, the remedy is different
             (wait for the call, versus ask your CA), and running them together
             would tell the reader neither. -->
        <div v-else-if="jobWindow && jobWindow.state !== 'open'" class="cp__closed" data-testid="chat-job-window">
          <div class="cp__closedh">
            <i class="fa fa-calendar" aria-hidden="true"></i>
            <strong>{{ jobWindow.state === 'before'
              ? 'Chat for this job opens ' + shortWhen(jobWindow.opensAt)
              : 'Chat for this job closed ' + shortWhen(jobWindow.closesAt) }}</strong>
          </div>
          <div class="cp__closedb">
            A job channel is open from 24 hours before the job starts until 24 hours after it ends.
            The conversation stays readable either side.
          </div>
          <div class="cp__fake">Message #{{ channel.name }}</div>
        </div>
        <div v-else-if="!mayPost" class="cp__closed" data-testid="chat-outside-hours">
          <div class="cp__closedh">
            <i class="fa fa-clock-o" aria-hidden="true"></i>
            <strong>Chat is read-only outside office hours{{ reopensAt ? ' — reopens ' + reopensAt : '' }}</strong>
          </div>
          <div class="cp__closedb">{{ closedReason }}</div>
          <div class="cp__fake">Message #{{ channel.name }}</div>
        </div>
        <div v-else class="cp__comp" :class="{ 'cp__atcap': draft.length >= 4000 }">
          <textarea v-model="draft" :maxlength="4000" :placeholder="'Message #' + channel.name"
                    :aria-label="'Message ' + channel.label" data-testid="chat-composer"></textarea>
          <div class="cp__compr">
            <div class="cp__rules">Plain text. 4,000 characters, 5 links, 30 posts a minute.</div>
            <div class="cp__cnt" :class="counterClass">{{ draft.length.toLocaleString('en') }} / 4,000</div>
            <button type="button" class="cp__send"
                    :disabled="!draft.trim() || draft.length >= 4000 || sending"
                    @click="send()">{{ sending ? 'Sending…' : 'Send' }}</button>
          </div>
          <div v-if="draft.length >= 4000" class="cp__compmsg">
            That’s the 4,000-character limit. Shorten the message to send it.
          </div>
          <!-- #2096 — why the last send did not land. Sits with the composer
               rather than in the header because it is about the message the
               user still has in front of them, and the draft is deliberately
               not cleared on a failure. -->
          <div v-else-if="liveError" class="cp__compmsg" data-testid="chat-send-error">
            {{ liveErrorText }}
          </div>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
/**
 * #2091 — the right-hand chat panel, behind CHAT_UI_PREVIEW.
 *
 * The approved wireframe (cs3-chat !4 round 4) rebuilt as a real component so Ed
 * can see it on real CS3 pages.
 *
 * ⚠ NO BACK END, AND NOTHING LEAVES THE BROWSER. There is no chat service yet
 * (cs3-chat #6-#10, #18): this file imports no service, makes no request, and
 * persists nothing. "Sending" appends to a local array that a reload discards.
 * Every name, channel and message comes from `chatPreview.js` and is invented.
 *
 * The one behaviour worth stating: messages render through Vue text
 * interpolation, never `v-html`, so a message cannot be interpreted as markup —
 * the rule the real panel has to keep, and one sample message exercises it.
 */
import {
    CHANNELS, GROUPS, PREVIEW_ROLES, ROLE_LABEL, ROLE_PLURAL, TODAY,
    buildHistory, canAdminChat, chatLiveEnabled, chatUiEnabled, historyFor,
    isOutsideHours, jobChatWindow, nextOpenLabel, previewStore, shortWhen, windowLabel,
} from '../../chatPreview.js';
// #2096 — the real chat service. Everything live goes through this module; the
// component holds no knowledge of the API or of Firestore.
import {
    ChatUnavailable, applyChannelDoc, colourFor, fetchChannels, initialsFor,
    listenToChannel, listenToChannelDocs, resetChatSession, sendMessage, signIn,
} from '../../chatService.js';
import { mapGetters } from 'vuex';

const MAX_SCROLLBACK = 120;

/*
 * #2096 — stands in for "no channel at all", so the channel view cannot throw
 * on a live build whose roster is empty. Frozen: it is shared, and nothing
 * should ever write to it.
 */
const EMPTY_CHANNEL = Object.freeze({
    id: null, name: '', label: '', grp: 'client', kind: null,
    archived: false, unread: 0, last: '', at: '—',
});
const PAGE_STEP = 35;

/**
 * Which Configuration group a channel belongs to, from its label. "Skills by
 * Status" is tested BEFORE "Skills" — it starts with the same word, and the
 * looser test first would swallow it and tie the wrong switch to it.
 */
function groupKeyFor(channel) {
    const label = channel.label;
    if (label.indexOf('Job') === 0) return 'Job';
    if (label.indexOf('Employers / Payroll') === 0) return 'Employers / Payroll';

    return label;
}

const DOCK_KEY = 'cs3-chat-dock-state';
const POPOUT_NAME = 'cs3ChatWindow';
/** Same width as the docked panel, so undocking does not reflow the content. */
const POPOUT_FEATURES = 'width=400,height=900,resizable=yes,scrollbars=yes';

export default {
    name: 'chatPanel',
    props: {
        /**
         * 'docked'  — the panel on a CS3 page (default)
         * 'window'  — the pop-out, filling its own browser window
         * One component in two shapes rather than two components: the channel
         * list, the window rules, the composer and the 120-message scrollback
         * are the same chat either way, and a second copy would drift.
         */
        mode: { type: String, default: 'docked' },
    },
    data() {
        return {
            collapsed: true,
            view: 'list',
            openId: 'c7_skill_audio',
            shown: 50,
            draft: '',
            sent: {},
            /*
             * ⚠ #2096 — EMPTY IN A LIVE BUILD, AND THAT MATTERS.
             *
             * Seeding the preview's channels unconditionally put invented
             * people, an invented local name and invented message previews in
             * front of real users for as long as the handoff took — and
             * permanently if it FAILED, because nothing would ever replace
             * them. With the "Preview as" strip hidden in a live build there
             * was nothing on screen marking any of it as fake.
             */
            channels: chatLiveEnabled() ? [] : JSON.parse(JSON.stringify(CHANNELS)),
            generated: buildHistory(),
            roleLabel: ROLE_LABEL,
            rolePlural: ROLE_PLURAL,
            previewRoles: PREVIEW_ROLES,
            // #2091 r5 — 'r:<role>' or 'p:<eid>'. null means "whoever is signed
            // in", so the panel behaves exactly as before until it is touched.
            viewAs: null,
            toast: null,
            toastLeft: 0,
            toastTimer: null,
            // The preview has no real chat identity, so the panel reads the
            // signed-in CS3 role and shows the channels that role would see.
            settings: previewStore(),
            // #2091 r4 — undock state, remembered for the session so a reload of
            // the main page does not silently re-dock a chat the user moved to
            // another monitor.
            undocked: false,
            popout: null,
            popoutWatch: null,
            popupBlocked: false,
            // Re-evaluated each minute so the panel crosses an opening or
            // closing time while someone is looking at it, rather than only on
            // a reload.
            now: new Date(),
            clock: null,

            /*
             * #2096 — live state. All of it stays null/empty in a preview
             * build, and every computed below asks `isLive` first, so the
             * preview path is byte-for-byte what Ed reviewed for #2091.
             */
            /*
             * ⚠ ONLY THE PLAIN FACTS ABOUT THE SIGNED-IN USER LIVE HERE, NOT
             * THE SESSION OBJECT.
             *
             * Vue 2 walks everything returned from `data()` and makes it
             * reactive in place, recursively. The session carries the Firebase
             * app and the Firestore instance, so putting it here would have Vue
             * rewriting the SDK's own internals with reactive getters — slow at
             * best, and at worst it breaks the client it is instrumenting.
             *
             * So the session lives on the instance as `this.chat`, assigned
             * outside `data()` and therefore never observed, and the handful of
             * values the template actually needs are copied here.
             */
            me: null,
            // 'idle' | 'signing-in' | 'ready' | 'failed'
            liveState: 'idle',
            // A ChatUnavailable code, shown to the user rather than logged away.
            liveError: null,
            // channelId -> the messages Firestore last sent for it.
            liveMessages: {},
            /*
             * The MESSAGE listener for the channel currently open — one, not
             * one per channel. See `watchOpenChannel`.
             */
            openListener: null,
            openListenerFor: null,
            // One unsubscribe covering every channel's own Firestore document.
            docListener: null,
            // Posting is a round trip; the composer is disabled while it runs
            // so one Enter cannot become two messages.
            sending: false,
        };
    },
    computed: {
        ...mapGetters({ userRoleSlug: 'userRoleSlug' }),
        /** #2096 — is this panel talking to the real chat service? */
        isLive() { return chatLiveEnabled(); },

        /**
         * The flag builds it in; the CA's switch turns it on. Chat off means the
         * panel and its launcher are GONE — not greyed out — which is the
         * wireframe's "chat off" state, reachable from Configuration -> Chat.
         *
         * ⚠ #2096 — IN A LIVE BUILD THE SWITCH IS THE SERVER'S ANSWER, not the
         * local preview store. `chat_visible` comes back resolved on the
         * handoff (cs3-chat HandoffExchange) because the client must not be the
         * one deciding whether a client's chat is on; the Firestore rules
         * enforce the same thing on reads, so a browser that ignored this would
         * see an empty panel rather than someone else's messages.
         *
         * While the handoff is still in flight the panel IS shown: it has a
         * signing-in state to display, and flashing the launcher away and back
         * on every page load is worse than a moment of "connecting".
         */
        visible() {
            if (!chatUiEnabled()) return false;
            if (!this.isLive) return this.settings.chatOn;
            if (this.liveState === 'ready') return !!(this.me && this.me.chatVisible);

            return true;
        },
        windowMode() { return this.mode === 'window'; },
        panelIsOpen() {
            // Undocked means the chat is NOT on this page, so the page gets its
            // full width back — the whole point of moving it to a second screen.
            return this.visible && !this.windowMode && !this.undocked && !this.collapsed;
        },

        /**
         * #2091 r3 — the job channel's 24h-either-side window, or null.
         *
         * #2096: this works on a live build. `ChannelAdmin::present()` sends
         * `starts_at`, `ends_at` and `own_option_on`, and `normaliseChannel`
         * shapes them into `job` — so the "Chat for this job opens Tue 08:00"
         * banner is the real window. The server still refuses an out-of-window
         * post with a 403; this is what puts the reason on screen first.
         */
        jobWindow() {
            return this.channel && this.channel.job ? jobChatWindow(this.channel.job, this.now) : null;
        },
        jobWindowLabel() { return windowLabel(this.jobWindow); },
        outside() { return isOutsideHours(this.settings.hours, this.now); },
        reopensAt() { return nextOpenLabel(this.settings.hours, this.now); },
        mayAdmin() { return canAdminChat(this.userRoleSlug); },
        /**
         * The signed-in CS3 role slug -> the chat role they would really see.
         *
         * ⚠ #2096 — IN A LIVE BUILD THE ROLE COMES FROM THE SESSION. The slug
         * map below is a guess made in the browser; the live role was decided
         * by `App\Chat\ChatRole` from a SIGNED claim and is the same value the
         * chat service recorded and the Firestore rules will enforce. Letting
         * the browser's guess win would show a channel list that disagrees with
         * what the user can actually read.
         */
        realRole() {
            if (this.isLive && this.me && this.me.role) {
                return this.me.role;
            }

            return {
                'client-admin': 'ca',
                'super-admin': 'cs_admin',
                'power-user': 'pu',
                'power-user-minus': 'pu',
                'steward': 'steward',
            }[this.userRoleSlug] || 'member';
        },
        /** #2091 r5 — the named people offered in the Preview-as picker. */
        namedPeople() { return this.settings.hours.people; },
        /** The named person being previewed, or null when previewing a role. */
        asPerson() {
            // #2096 — there are no previewed people in a live build.
            if (this.isLive) return null;
            if (!this.viewAs || this.viewAs.slice(0, 2) !== 'p:') return null;
            const eid = Number(this.viewAs.slice(2));

            return this.namedPeople.find((x) => x.eid === eid) || null;
        },
        /**
         * The chat role in force. A previewed PERSON brings their own role with
         * them, so picking Dana Whitfield shows what a Steward sees.
         */
        role() {
            /*
             * ⚠ #2096 — A LIVE BUILD IGNORES `viewAs` ENTIRELY.
             *
             * Not just because the picker is hidden: `mounted` seeds `viewAs`
             * from `realRole`, and at mount the session has not arrived, so the
             * seed is the slug-map guess ('member' for most people). Honouring
             * it afterwards would pin the panel to that guess even once the
             * server had said 'ca' — a Client Admin would silently see a
             * Member's channel list for the rest of the session.
             */
            if (this.isLive) return this.realRole;

            if (this.asPerson) return this.asPerson.role;
            if (this.viewAs && this.viewAs.slice(0, 2) === 'r:') return this.viewAs.slice(2);

            return this.realRole;
        },
        viewAsNote() {
            if (this.asPerson) {
                return 'named individually — overrides the ' + (ROLE_PLURAL[this.asPerson.role] || 'role') + ' switch';
            }
            if (this.role === this.realRole) return 'your own role';

            return 'preview only — you are signed in as ' + (ROLE_PLURAL[this.realRole] || this.realRole);
        },
        isStaff() { return this.role === 'cs_admin' || this.role === 'cs_support'; },
        granted() { return this.role === 'ca' || this.role === 'pu'; },
        visibleChannels() {
            /*
             * ⚠ #2096 — IN A LIVE BUILD THERE IS NOTHING TO FILTER HERE.
             *
             * Every rule the preview re-implements below — role visibility,
             * the group switches, the job window, the two master switches, the
             * global grant — is applied by the chat service when it builds the
             * list (`ChatPolicy`), and again by the Firestore rules on read. A
             * second copy of those rules in the browser could only ever
             * DISAGREE with the authority, and the direction it disagrees in is
             * not knowable from here: hiding a channel the user may read looks
             * like data loss, and showing one they may not is a listener that
             * fails silently and bills reads while it retries.
             *
             * So the live list is the server's list, unfiltered. The preview
             * keeps its filters because making those switches visibly do
             * something is the whole point of #2091.
             */
            if (this.isLive) return this.channels;

            return this.channels.filter((c) => {
                if (c.see.indexOf(this.role) < 0) return false;
                // A group switched off in Configuration -> Chat takes its
                // channels out of the list, which is how that switch is seen to
                // do something.
                if (c.grp === 'client' && this.settings.groups[groupKeyFor(c)] === false) return false;

                // #2091 r3 — Job and Employers/Payroll are MASTER switches. The
                // client switch only PERMITS the channel; the job's or
                // employer's own Chat option is the other half, and a channel
                // exists only where both are on.
                if (c.job && !c.job.chatOn) return false;
                if (c.employer && !c.employer.chatOn) return false;
                if (c.grp === 'global' && c.needsGrant && !this.isStaff && this.role !== 'ca' && !this.granted) return false;

                return true;
            });
        },
        groupsShown() {
            /*
             * #2096 — the client group's heading is the PREVIEW's invented local
             * name ('Harbourview Stage Local'), which must not appear above a
             * real client's channels. The live heading is the client's own
             * name; until the chat service sends one it is a neutral label
             * rather than someone else's.
             */
            const groups = this.isLive
                ? GROUPS.map((g) => (g.key === 'client' ? { ...g, title: this.liveClientTitle } : g))
                : GROUPS;

            return groups
                .map((g) => ({ ...g, rows: this.visibleChannels.filter((c) => c.grp === g.key) }))
                .filter((g) => g.rows.length);
        },

        /** The heading over a live build's client channels. */
        liveClientTitle() {
            return (this.me && this.me.clientName) || 'Your local';
        },
        unreadTotal() { return this.visibleChannels.reduce((n, c) => n + c.unread, 0); },
        /*
         * #2096 — can be undefined when a live roster is empty (chat on, no
         * channels yet). `view` only advances through `openChannel`, so the
         * channel branch of the template is unreachable in that state today —
         * but it is one empty list away from being reachable, and the template
         * reads `channel.archived` and `channel.name` unguarded. Falling back
         * to an inert placeholder keeps that a blank panel rather than a
         * render error that takes the page with it.
         */
        channel() {
            return this.channels.find((c) => c.id === this.openId)
                || this.visibleChannels[0]
                || EMPTY_CHANNEL;
        },
        history() {
            // #2096 — live messages come from Firestore, already oldest-first
            // and already shaped by chatService.normaliseMessage.
            if (this.isLive) {
                return (this.channel && this.liveMessages[this.channel.id]) || [];
            }

            const me = { name: 'You', initials: 'ME', role: this.role, colour: '#3f6bbf' };

            return historyFor(this.channel, this.generated, me).concat(this.sent[this.channel.id] || []);
        },
        slice() { return this.history.slice(Math.max(0, this.history.length - this.shown)); },
        shownCount() { return Math.min(this.shown, this.history.length); },
        atCap() { return this.shown >= MAX_SCROLLBACK; },
        headTitle() { return this.view === 'list' ? 'Chat' : '#' + this.channel.name; },
        headSub() {
            if (this.view === 'list') {
                /*
                 * #2096 — in a live build this line is the connection state,
                 * because "why is this empty" is the question the user will
                 * have and the answer is never the fake client's name.
                 */
                if (this.isLive) {
                    if (this.liveState === 'signing-in') return 'Connecting…';
                    if (this.liveState === 'failed') return this.liveErrorText;

                    return (this.me && this.me.name) || 'Chat';
                }

                return 'Harbourview Stage Local · preview';
            }
            // A job channel's header carries its window, so the reason it is or
            // is not chatable is on screen rather than inferred.
            if (this.jobWindowLabel) return this.jobWindowLabel;

            return this.channel.label + (this.channel.archived ? ' · archived' : '');
        },

        /**
         * #2096 — a ChatUnavailable code turned into something a dispatcher can
         * act on.
         *
         * Every one of these is a real state the chat service can put the panel
         * in, and they want different responses from the user: wait, reload,
         * shorten the message, or tell the office. A single "chat error" would
         * collapse all of that — and the service's own 401 is deliberately
         * indistinguishable between causes, so this is the only layer that can
         * say anything useful at all.
         */
        liveErrorText() {
            return {
                no_logged_in_client: 'Chat needs a local selected. Pick one and reload.',
                handoff_rejected: 'Chat sign-in expired. Reload the page to try again.',
                chat_session_expired: 'Chat sign-in expired. Reload the page to try again.',
                chat_unreachable: 'Chat is unreachable right now.',
                chat_unavailable: 'Chat is not configured on this server.',
                cs3_handoff_incomplete: 'Chat is not configured on this server.',
                chat_session_missing: 'Chat is not configured on this server.',
                chat_listen_failed: 'Lost the connection to chat. Reload the page.',
                // The UUID/integer contract gap — see ChatHandoff::cid. Worded
                // for the person who will see it on stage- before that is
                // settled, so it does not read as a network fault.
                chat_client_not_addressable: 'Chat cannot address this local yet. Tell the office.',
                chat_not_permitted: 'You do not have access to chat for this local.',
                // Not shown in practice: the panel that would display it is
                // being torn down. Here so the code is never a bare fallback.
                chat_sign_in_abandoned: 'Chat sign-in was interrupted.',
                posting_closed: 'Posting is closed in this channel.',
                message_too_long: 'That is over the 4,000-character limit.',
                message_rejected: 'That message was refused.',
                too_many_messages: 'Too many messages just now — wait a moment.',
            }[this.liveError] || 'Chat is unavailable.';
        },
        counterClass() {
            if (this.draft.length >= 4000) return 'cp__over';

            return this.draft.length >= 3600 ? 'cp__warn' : '';
        },
        /**
         * Precedence: person beats role beats schedule, exactly as the wireframe
         * and spec row 21 have it. CS staff are never restricted.
         */
        mayPost() {
            /*
             * ⚠ #2096 — A LIVE BUILD HAS NO OFFICE-HOURS DATA YET, SO IT MUST
             * NOT PRETEND TO.
             *
             * The schedule, the role switches and the named exceptions live on
             * the chat service (`GET /clients/{id}/settings`), and reading them
             * is Configuration → Chat's own issue. Everything below this line
             * would otherwise be evaluated against the PREVIEW's invented
             * schedule — a Monday-to-Saturday 08:00–18:00 default that has
             * nothing to do with the real client — and would close the composer
             * for real users at times their CA never set.
             *
             * So the live panel does not decide this. The chat service refuses
             * an out-of-hours post with a 403, which `sendLive` surfaces as
             * "Posting is closed in this channel." That is the authority either
             * way: the panel's banner was always a courtesy, never the gate.
             *
             * An archived channel and a job window ARE decided here, because
             * both facts come from the channel the server sent.
             */
            if (this.jobWindow && this.jobWindow.state !== 'open') return false;
            if (this.isLive) return true;
            if (!this.outside) return true;
            if (this.isStaff) return true;

            // #2091 r5 — PERSON BEATS ROLE, and it is now the previewed PERSON.
            // This used to look up a named person by matching their ROLE against
            // the viewed role, because the panel had no identity to match on. The
            // screen claims "person, then role, then the schedule", and that
            // approximation contradicted it out loud: Dana Whitfield is named
            // allowed and is a Steward, so with the Stewards switch OFF every
            // steward could still post and the switch looked broken.
            if (this.asPerson) return this.asPerson.allowed;

            return this.settings.hours.roles[this.role] === true;
        },
        closedReason() {
            if (this.asPerson) {
                return 'Your Client Admin has not allowed ' + this.asPerson.name
                    + ' to post outside office hours.';
            }

            return 'Your Client Admin has not allowed ' + (ROLE_PLURAL[this.role] || 'people in your role')
                + ' to post outside office hours.';
        },
    },
    methods: {
        shortWhen,

        /** Open the chat in its own window, and stand down the docked panel. */
        undock() {
            this.popupBlocked = false;
            const url = window.location.origin + window.location.pathname + '#/chat-window';
            const win = window.open(url, POPOUT_NAME, POPOUT_FEATURES);

            // A blocked pop-up returns null (or a window that is already closed).
            // Say so and stay docked rather than leaving the user with a chat
            // that silently went nowhere.
            if (!win || win.closed) {
                this.popupBlocked = true;

                return;
            }

            this.popout = win;
            this.setUndocked(true);
            this.watchPopout();
            try { win.focus(); } catch (e) { /* focus is best effort */ }
        },

        /** Bring it back: close the pop-out if we still have a handle on it. */
        dock() {
            if (this.popout && !this.popout.closed) {
                try { this.popout.close(); } catch (e) { /* it may be gone already */ }
            }
            this.popout = null;
            this.stopWatchingPopout();
            this.setUndocked(false);
            this.collapsed = false;
        },

        /** Dock pressed INSIDE the pop-out: tell the opener, then close. */
        dockFromWindow() {
            try {
                if (window.opener && !window.opener.closed) {
                    window.opener.postMessage({ type: 'cs3-chat-dock' }, window.location.origin);
                }
            } catch (e) { /* opener gone or cross-origin; closing still re-docks */ }
            window.close();
        },

        setUndocked(value) {
            this.undocked = value;
            try {
                window.sessionStorage.setItem(DOCK_KEY, value ? 'undocked' : 'docked');
            } catch (e) { /* private mode: the state just does not survive a reload */ }
        },

        /**
         * Closing the pop-out by its own X must re-dock too, and a window has no
         * event the opener can listen to for that — so poll `closed`. Cheap, and
         * the only thing that works across a user-closed window.
         */
        watchPopout() {
            this.stopWatchingPopout();
            this.popoutWatch = setInterval(() => {
                if (!this.popout || this.popout.closed) {
                    this.popout = null;
                    this.stopWatchingPopout();
                    this.setUndocked(false);
                }
            }, 500);
        },

        stopWatchingPopout() {
            if (this.popoutWatch) {
                clearInterval(this.popoutWatch);
                this.popoutWatch = null;
            }
        },

        onDockMessage(event) {
            // Same-origin only, and only our own message shape.
            if (event.origin !== window.location.origin) return;
            if (!event.data || event.data.type !== 'cs3-chat-dock') return;
            this.dock();
        },
        dayOf(i) { return i < 0 ? null : (this.slice[i] || {}).day; },
        isCont(i) {
            const prev = this.slice[i - 1];

            return !!prev && prev.who.n === this.slice[i].who.n && prev.day === this.slice[i].day;
        },
        openChannel(id) {
            this.openId = id;
            this.view = 'channel';
            this.shown = 50;
            this.draft = '';
            const c = this.channels.find((x) => x.id === id);
            if (c) c.unread = 0;
            // #2096 — start reading this channel. Opening one channel does not
            // stop the others: the channel-list previews and the toast both
            // depend on messages arriving for channels nobody is looking at.
            if (this.isLive) this.watchOpenChannel();
            this.$nextTick(() => this.scrollToNewest());
        },
        loadOlder() {
            this.shown = Math.min(MAX_SCROLLBACK, this.shown + PAGE_STEP);
            /*
             * #2096 — scrollback in a live build means a WIDER query, not a
             * slice of something already held: Firestore only sent `shown`
             * documents. Re-listening replaces the listener for this channel
             * (watchChannel detaches the old one first), so the window grows
             * without accumulating listeners.
             *
             * The ceiling is the chat service's, not a UI preference — the
             * Firestore rules refuse any single query over 120 (spec decision
             * #14), and anything older is recovered from the chat database by a
             * CA or PU through Reports.
             */
            if (this.isLive) this.watchOpenChannel();
            // Land on what just loaded, which at the ceiling is the Reports note.
            this.$nextTick(() => { if (this.$refs.body) this.$refs.body.scrollTop = 0; });
        },
        scrollToNewest() {
            if (this.$refs.body) this.$refs.body.scrollTop = this.$refs.body.scrollHeight;
        },
        send() {
            const text = this.draft.trim();
            if (!text) return;

            // #2096 — a live build posts to the chat service.
            if (this.isLive) {
                this.sendLive(text);

                return;
            }

            const list = this.sent[this.channel.id] || [];
            // Local only. There is nowhere to send it to, and that is the point.
            list.push({
                who: { n: 'You', i: 'ME', r: this.role, c: '#3f6bbf' },
                day: TODAY,
                at: new Date().toTimeString().slice(0, 5),
                body: text,
            });
            this.$set(this.sent, this.channel.id, list);
            this.draft = '';
            this.$nextTick(() => this.scrollToNewest());
        },

        // ── #2096: the live path ────────────────────────────────────────────

        /**
         * Sign in to chat and load the channel list.
         *
         * ⚠ NOTHING HERE THROWS PAST THIS METHOD. Chat is a panel beside the
         * app, not the app: a chat server that is down, misconfigured or
         * unreachable must leave dispatch entirely usable. Every failure
         * becomes `liveState = 'failed'` plus a line the user can read.
         */
        async startLive() {
            if (this.liveState === 'signing-in' || this.liveState === 'ready') return;

            this.liveState = 'signing-in';
            this.liveError = null;

            try {
                const session = await signIn();
                // Deliberately NOT in `data()` — see the `me` comment there.
                this.chat = session;
                this.me = {
                    uid: session.uid,
                    role: session.role,
                    cid: session.cid,
                    name: session.name,
                    clientName: session.clientName,
                    chatVisible: session.chatVisible,
                };

                // The CA's master switch is off for this client: there is
                // nothing to list and nothing to listen to. `visible` hides
                // the panel; this just stops us asking.
                if (!session.chatVisible) {
                    this.liveState = 'ready';

                    return;
                }

                const channels = await fetchChannels(session);
                this.channels = channels;
                this.liveState = 'ready';

                if (channels.length && !channels.some((c) => c.id === this.openId)) {
                    // The preview's default openId does not exist on a real
                    // server; land on the first channel we can actually read.
                    this.openId = channels[0].id;
                }

                /*
                 * ⚠ TWO DIFFERENT LISTENERS, FOR TWO DIFFERENT JOBS.
                 *
                 *   every channel's DOC   the list previews, the unread counts
                 *                         and the toast — all of which describe
                 *                         channels nobody is looking at, so this
                 *                         cannot be narrowed to the open one
                 *   the open channel's
                 *   MESSAGES              the conversation on screen
                 *
                 * Attaching a 50-message listener to every channel instead
                 * costs roughly fifty times as much on every mount (spec §12
                 * prices a channel doc at 1 read and opening a channel at ~50),
                 * and Firestore has no hard spend cap.
                 */
                await this.watchChannelDocs(channels.map((c) => c.id));
                if (this.openId) this.watchOpenChannel();
            } catch (e) {
                this.liveState = 'failed';
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                // Logged as well as shown: the code alone does not say which
                // request failed, and this is the only place that knows.
                console.warn('[chat] sign-in failed', e);
            }
        },

        /**
         * Listen to every channel's own Firestore document.
         *
         * One subscription covering all of them, replaced wholesale when the
         * roster changes — the roster only changes on a fresh sign-in, so this
         * is attached once per session.
         */
        async watchChannelDocs(channelIds) {
            if (!this.chat || !this.chat.firestore || !channelIds.length) return;

            this.detachDocs();

            try {
                this.docListener = await listenToChannelDocs(
                    this.chat, channelIds,
                    (id, docData) => this.onChannelDoc(id, docData),
                    (id, err) => {
                        this.liveError = err.code;
                        console.warn('[chat] channel-doc listener failed on ' + id, err);
                    }
                );
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_listen_failed';
                console.warn('[chat] could not watch channel docs', e);
            }
        },

        /**
         * Listen to the OPEN channel's messages, at the current scrollback.
         *
         * Idempotent and single: the previous message listener is detached
         * first, whichever channel it belonged to. Called on open and again on
         * every `loadOlder`, because widening the scrollback in Firestore means
         * a wider query rather than a slice of something already held.
         */
        async watchOpenChannel() {
            if (!this.chat || !this.chat.firestore || !this.openId) return;

            const channelId = this.openId;
            this.detachOpen();

            try {
                const stop = await listenToChannel(
                    this.chat, channelId, this.shown,
                    (messages) => this.onLiveMessages(channelId, messages),
                    (err) => {
                        this.liveError = err.code;
                        console.warn('[chat] listener failed on ' + channelId, err);
                    }
                );

                /*
                 * The user can open another channel while this await is in
                 * flight. Publishing then would leave the newer listener
                 * orphaned and this stale one feeding the panel, so the late
                 * arrival is dropped instead.
                 */
                if (this.openId !== channelId) {
                    stop();

                    return;
                }

                this.openListener = stop;
                this.openListenerFor = channelId;
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_listen_failed';
                console.warn('[chat] could not listen to ' + channelId, e);
            }
        },

        detachOpen() {
            if (!this.openListener) return;
            try { this.openListener(); } catch (e) { /* already gone */ }
            this.openListener = null;
            this.openListenerFor = null;
        },

        detachDocs() {
            if (!this.docListener) return;
            try { this.docListener(); } catch (e) { /* already gone */ }
            this.docListener = null;
        },

        /**
         * A channel's document changed.
         *
         * This is the ONLY source of unread counts and toasts, because it is
         * the only listener attached to channels the user is not looking at.
         */
        onChannelDoc(channelId, docData) {
            const channel = this.channels.find((c) => c.id === channelId);
            if (!channel) return;

            const isNewMessage = applyChannelDoc(channel, docData);
            if (!isNewMessage) return;

            /*
             * Not mine, and not the channel on screen. `lastSenderUid` rather
             * than the display name: two people can share a name, and a toast
             * for your own message is the kind of small wrongness that makes a
             * feature feel broken.
             */
            const mine = this.me && channel.lastSenderUid === this.me.uid;
            const looking = !this.collapsed && this.view === 'channel' && this.openId === channelId;

            if (mine || looking) {
                if (looking) channel.unread = 0;

                return;
            }

            channel.unread = (channel.unread || 0) + 1;
            this.showLiveToast(channel, docData);
        },

        /**
         * Firestore sent the open channel's messages.
         *
         * Messages only. The channel-list preview, the unread count and the
         * toast are all driven by `onChannelDoc`, because they describe
         * channels this listener is not attached to.
         */
        onLiveMessages(channelId, messages) {
            this.$set(this.liveMessages, channelId, messages);

            // Reading a channel clears its count, whatever the doc listener
            // counted while it was closed.
            const channel = this.channels.find((c) => c.id === channelId);
            const looking = !this.collapsed && this.view === 'channel' && this.openId === channelId;
            if (channel && looking) channel.unread = 0;
        },

        /**
         * The 60-second toast (spec §5), for a real message.
         *
         * Built from the channel DOCUMENT's `lastSender` / `lastSnippet`, which
         * is what spec §7 says those fields are for — the toast has to work for
         * a channel with no message listener attached, so the message itself is
         * not available here and must not be fetched for it.
         *
         * Separate from the preview's `showToast`, which invents one on demand
         * from a fixed channel: that is a demo control, this is an event. They
         * share the timer so the two can never both be up.
         */
        showLiveToast(channel, docData) {
            const who = (docData && docData.lastSender) || 'CallSteward';

            this.toast = {
                where: channel.name,
                whereId: channel.id,
                who: { n: who, i: initialsFor(who), c: colourFor(channel.lastSenderUid || who) },
                body: (docData && docData.lastSnippet) || '',
            };
            this.toastLeft = 60;
            clearInterval(this.toastTimer);
            this.toastTimer = setInterval(() => {
                this.toastLeft -= 1;
                if (this.toastLeft <= 0) this.hideToast();
            }, 1000);
        },

        /**
         * Post a message for real.
         *
         * ⚠ NO OPTIMISTIC ECHO. The sent message appears when Firestore sends
         * it back, which is the same path everyone else's messages take. An
         * optimistic line would have to be reconciled with the real one on
         * arrival (same message, different id, possibly sanitised differently —
         * spec §5 strips formatting and rewrites look-alike hostnames), and a
         * message shown as sent that the server then refused is worse than a
         * quarter-second of nothing.
         *
         * The draft is only cleared once the server has it, for the same
         * reason: a refused message the user has to retype is a lost message.
         */
        async sendLive(text) {
            if (this.sending || !this.chat || !this.channel) return;

            this.sending = true;
            this.liveError = null;

            try {
                await sendMessage(this.chat, this.channel.id, text);
                this.draft = '';
                this.$nextTick(() => this.scrollToNewest());
            } catch (e) {
                this.liveError = (e instanceof ChatUnavailable) ? e.code : 'chat_unreachable';
                console.warn('[chat] send failed', e);
            } finally {
                this.sending = false;
            }
        },
        openSettings() {
            this.collapsed = true;
            if (this.$route.path !== '/configuration') this.$router.push({ path: '/configuration' });
        },
        showToast() {
            const c = this.channels.find((x) => x.id === 'c7_job_18420');
            if (!c || c.see.indexOf(this.role) < 0) return;
            this.toast = {
                where: c.name,
                whereId: c.id,
                who: { n: 'Dana Whitfield', i: 'DW', c: '#7a5bb5' },
                body: 'Gate code changed for tomorrow’s load-in — it’s in the job notes now.',
            };
            this.toastLeft = 60;
            clearInterval(this.toastTimer);
            this.toastTimer = setInterval(() => {
                this.toastLeft -= 1;
                if (this.toastLeft <= 0) this.hideToast();
            }, 1000);
        },
        hideToast() {
            clearInterval(this.toastTimer);
            this.toastTimer = null;
            this.toast = null;
        },
        openFromToast() {
            const id = this.toast && this.toast.whereId;
            this.hideToast();
            this.collapsed = false;
            if (id) this.openChannel(id);
        },
    },
    created() {
        /*
         * #2096 — the live chat session, declared OUTSIDE `data()` on purpose.
         *
         * It holds the Firebase app and the Firestore instance, and Vue 2
         * recursively makes everything in `data()` reactive — which would mean
         * rewriting the SDK's internals with reactive getters. Declared here so
         * the field exists from the start rather than appearing mid-flight, and
         * so the reason is recorded next to it.
         *
         * Nothing in a template or computed may read this. The plain values the
         * UI needs are mirrored into `me`, which IS reactive.
         */
        this.chat = null;
    },
    watch: {
        /**
         * #2091 r2 — tell App.vue when the panel is open so the layout can make
         * room: the left rail slides away and the middle gets both its 60px and
         * clearance from this panel.
         *
         * `visible` is in the condition, not just `collapsed`: chat being
         * switched off in Configuration hides the panel without touching
         * `collapsed`, and without this the page would keep a 368px gutter
         * reserved for a panel that is no longer there.
         *
         * `immediate` because the panel can mount already open — a reload after
         * the user opened it — and the layout has to agree from the first paint.
         */
        panelIsOpen: { handler(open) { this.$emit('panel-open', open); }, immediate: true },
    },
    mounted() {
        this.clock = setInterval(() => { this.now = new Date(); }, 60000);

        /*
         * #2096 — sign in to chat as soon as the panel exists, collapsed or
         * not. The channel list, the unread counts and the toast all have to be
         * right BEFORE the user opens the panel — that is what the launcher's
         * unread badge is — so waiting for the first open would show a stale
         * zero and no toast until then.
         *
         * Not awaited: nothing on this page may wait for a chat server.
         */
        // The pop-out is a separate browser window with its own module state,
        // so it signs in for itself rather than inheriting anything.
        if (this.isLive) this.startLive();

        // #2091 r5 — seat the Preview-as picker on the signed-in role, so it
        // opens showing what this user really sees and changes nothing until it
        // is touched. Done here rather than in data() because the role comes
        // from a vuex getter.
        if (!this.viewAs) this.viewAs = 'r:' + this.realRole;

        if (this.windowMode) return;   // the pop-out neither owns nor watches one

        try {
            this.undocked = window.sessionStorage.getItem(DOCK_KEY) === 'undocked';
        } catch (e) { /* private mode: start docked */ }

        // A reload of the main page loses the window handle but not the state.
        // Re-acquire it by name: window.open with an existing name and no URL
        // returns the SAME window without navigating it, so the pop-out is not
        // reloaded out from under the user. If it is gone, we re-dock.
        if (this.undocked) {
            let existing = null;
            try { existing = window.open('', POPOUT_NAME); } catch (e) { existing = null; }
            if (existing && !existing.closed && existing !== window) {
                this.popout = existing;
                this.watchPopout();
            } else {
                this.setUndocked(false);
            }
        }

        window.addEventListener('message', this.onDockMessage);
    },
    beforeDestroy() {
        clearInterval(this.toastTimer);
        clearInterval(this.clock);
        this.stopWatchingPopout();
        window.removeEventListener('message', this.onDockMessage);
        /*
         * #2096 — detach every Firestore listener.
         *
         * ⚠ NOT OPTIONAL HOUSEKEEPING. A listener outlives the component that
         * made it, keeps its connection open, and keeps charging reads —
         * Firestore has no hard spend cap (spec §12), which is why a budget
         * alert on each project is required rather than advisable. Navigating
         * between CS3 pages with the panel mounted would otherwise leave one
         * listener per channel per visit, for the life of the tab.
         *
         * The session itself is dropped too: its handoff was single-use and its
         * listeners are gone, so keeping it would only hand a stale object to
         * the next mount.
         */
        this.detachOpen();
        this.detachDocs();
        if (this.isLive) resetChatSession();
        this.chat = null;
        // Leaving the page with the panel open must not strand the layout in
        // its made-room state.
        this.$emit('panel-open', false);
    },
};
</script>

<style scoped>
.cp__rail{
    position:fixed;right:20px;bottom:20px;z-index:1040;
    display:flex;align-items:center;gap:9px;
    padding:11px 17px 11px 15px;
    background:#1a2744;color:#fff;border:0;border-radius:24px;
    box-shadow:0 5px 18px rgba(26,39,68,.34);font-size:13.5px;font-weight:600;cursor:pointer;
}
.cp__rail:hover{background:#243654}
.cp__railn{
    min-width:19px;padding:1px 6px;border-radius:9px;background:#fb8b03;color:#3d2200;
    font-size:10.5px;font-weight:700;text-align:center;
}

.cp__panel{
    position:fixed;top:0;right:0;bottom:0;z-index:1041;width:368px;max-width:100vw;
    display:flex;flex-direction:column;
    background:#fff;border-left:1px solid #dcdfe6;box-shadow:-6px 0 22px rgba(26,39,68,.16);
}
.cp__head{
    display:flex;align-items:center;gap:8px;padding:10px 8px 10px 14px;min-height:48px;
    background:#1a2744;color:#fff;
}
.cp__title{flex:1;min-width:0}
.cp__t1{display:block;font-size:14px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__t2{display:block;font-size:11px;color:#9fb3d6;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__ico{
    width:30px;height:30px;flex:0 0 30px;border-radius:4px;display:grid;place-items:center;
    background:transparent;border:0;color:#cfdcf0;cursor:pointer;
}
.cp__ico:hover{background:rgba(255,255,255,.12);color:#fff}
.cp__body{flex:1;min-height:0;overflow-y:auto;overflow-x:hidden}

.cp__grp{padding:12px 0 2px}
.cp__grph{
    display:flex;align-items:center;gap:7px;padding:0 14px 6px;
    font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.085em;color:#6b7280;
}
.cp__staff{
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9.5px;font-weight:400;
    text-transform:none;color:#8b949e;border:1px solid #dcdfe6;border-radius:3px;padding:1px 4px;
}
.cp__ch{
    display:flex;align-items:flex-start;gap:9px;width:100%;padding:8px 14px;text-align:left;
    background:none;border:0;border-left:3px solid transparent;cursor:pointer;
}
.cp__ch:hover{background:#f6f8fb}
.cp__ch.cp__on{background:#eef4ff;border-left-color:#2451a8}
.cp__hash{flex:0 0 15px;margin-top:2px;color:#8b949e;font-family:ui-monospace,Menlo,monospace;font-size:13px}
.cp__chmain{flex:1;min-width:0}
.cp__chn{display:flex;align-items:baseline;gap:6px;font-size:13px;color:#222;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__unread .cp__chn{font-weight:700}
.cp__arch{
    flex:0 0 auto;font-family:ui-monospace,Menlo,monospace;font-size:9.5px;color:#6b7280;
    background:#e8ebf0;border-radius:3px;padding:1px 4px;
}
.cp__chs{display:block;font-size:11.5px;color:#6b7280;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.cp__chr{flex:0 0 auto;display:flex;flex-direction:column;align-items:flex-end;gap:4px;margin-top:1px}
.cp__cht{font-family:ui-monospace,Menlo,monospace;font-size:10px;color:#8b949e}
.cp__pill{
    min-width:19px;padding:1px 6px;border-radius:9px;background:#2451a8;color:#fff;
    font-family:ui-monospace,Menlo,monospace;font-size:10.5px;font-weight:700;text-align:center;
}
.cp__note{
    margin:10px 14px;padding:10px 12px;background:#f4f7f8;border:1px solid #dcdfe6;border-radius:4px;
    font-size:12px;color:#4a5562;line-height:1.5;
}

.cp__older{margin:10px 14px 4px;display:flex;flex-direction:column;gap:7px;align-items:flex-start}
.cp__olderb{
    padding:6px 12px;background:#fff;color:#2451a8;border:1px solid #dcdfe6;border-radius:4px;
    font-size:12px;font-weight:600;cursor:pointer;
}
.cp__olderb:hover{border-color:#2451a8;background:#eef4ff}
.cp__olderc{font-family:ui-monospace,Menlo,monospace;font-size:10.5px;color:#8b949e}
.cp__cap{
    margin:10px 14px 6px;padding:10px 12px;background:#eef4ff;border:1px solid #c9d9f5;
    border-left:3px solid #2451a8;border-radius:4px;font-size:12px;color:#1a2744;line-height:1.5;
}
.cp__cap strong{display:block;margin-bottom:2px}
.cp__where{font-family:ui-monospace,Menlo,monospace;font-size:11px;color:#2451a8}

.cp__day{
    display:flex;align-items:center;gap:10px;margin:14px 14px 8px;
    font-family:ui-monospace,Menlo,monospace;font-size:10px;letter-spacing:.06em;
    text-transform:uppercase;color:#8b949e;
}
.cp__day::before,.cp__day::after{content:"";flex:1;height:1px;background:#dcdfe6}

.cp__msg{display:flex;gap:9px;padding:5px 14px}
.cp__msg:hover{background:#fafbfd}
.cp__cont{padding-top:1px}
.cp__av{
    flex:0 0 28px;width:28px;height:28px;border-radius:4px;margin-top:2px;
    display:grid;place-items:center;color:#fff;font-size:10.5px;font-weight:700;
}
.cp__mmain{flex:1;min-width:0}
.cp__mh{display:flex;align-items:baseline;gap:7px;flex-wrap:wrap;margin-bottom:1px}
.cp__mn{font-size:12.5px;font-weight:700;color:#222}
/* #2091 r5 — the Preview-as strip. Deliberately plain and slightly inset from
   the header so it reads as scaffolding for the preview rather than as chat
   chrome somebody might expect to ship. */
.cp__viewas{
  display:flex;align-items:center;gap:8px;flex-wrap:wrap;
  padding:6px 10px;border-bottom:1px solid #e4e7eb;background:#f7f8fa;
  font-size:11px;color:#52606d;
}
.cp__vl{font-weight:600;text-transform:uppercase;letter-spacing:.06em;white-space:nowrap}
.cp__vs{
  flex:1 1 120px;min-width:0;font:inherit;font-size:12px;color:#1f2933;
  padding:3px 6px;border:1px solid #cbd2d9;border-radius:4px;background:#fff;
}
.cp__vn{flex:1 1 100%;font-style:italic;color:#7b8794}
.cp__mrole{
    font-family:ui-monospace,Menlo,monospace;font-size:9.5px;padding:1px 4px;border-radius:3px;
    background:#e8ebf0;color:#4a5562;
}
.cp__staffrole{background:#eef4ff;color:#2451a8}
.cp__mt{font-family:ui-monospace,Menlo,monospace;font-size:10px;color:#8b949e}
.cp__mb{font-size:13.5px;line-height:1.46;color:#222;overflow-wrap:anywhere;white-space:pre-wrap}

.cp__lp{
    display:flex;gap:10px;align-items:flex-start;margin:6px 0 2px;padding:9px 10px;max-width:290px;
    background:#f4f7f8;border:1px solid #dcdfe6;border-left:3px solid #2451a8;border-radius:4px;
}
.cp__lpic{
    flex:0 0 26px;width:26px;height:26px;border-radius:3px;display:grid;place-items:center;
    background:#fff;border:1px solid #dcdfe6;font-size:12px;font-weight:700;color:#2451a8;
}
.cp__lpm{min-width:0}
.cp__lpt{font-size:12.5px;font-weight:600;color:#222;line-height:1.3}
.cp__lph{font-family:ui-monospace,Menlo,monospace;font-size:10.5px;color:#6b7280;margin-top:2px;overflow-wrap:anywhere}
.cp__lpw{margin-top:4px;font-size:10.5px;color:#9a6a00}
.cp__sani{
    display:flex;align-items:flex-start;gap:6px;margin-top:5px;
    font-family:ui-monospace,Menlo,monospace;font-size:10px;line-height:1.5;color:#6b7280;
}
.cp__sani::before{content:"";flex:0 0 5px;width:5px;height:5px;border-radius:50%;background:#fb8b03;margin-top:5px}

.cp__comp{border-top:1px solid #dcdfe6;padding:9px 12px 10px;background:#fff}
.cp__comp textarea{
    width:100%;min-height:62px;max-height:140px;resize:vertical;padding:8px 9px;
    font-size:13.5px;line-height:1.45;color:#222;background:#fff;border:1px solid #dcdfe6;border-radius:4px;
}
.cp__comp textarea:focus{border-color:#2451a8;outline:none}
.cp__atcap textarea{border-color:#a8252e}
.cp__compr{display:flex;align-items:center;gap:10px;margin-top:7px}
.cp__rules{flex:1;min-width:0;font-size:10.5px;color:#8b949e;line-height:1.35}
.cp__cnt{font-family:ui-monospace,Menlo,monospace;font-size:11px;color:#6b7280;white-space:nowrap}
.cp__warn{color:#9a6a00;font-weight:700}
.cp__over{color:#a8252e;font-weight:700}
.cp__send{
    padding:7px 15px;background:#2451a8;color:#fff;border:1px solid #2451a8;border-radius:4px;
    font-size:13px;font-weight:600;cursor:pointer;
}
.cp__send:hover{background:#1b3f85;border-color:#1b3f85}
.cp__send:disabled{background:#aab4c4;border-color:#aab4c4;cursor:not-allowed}
.cp__compmsg{margin-top:6px;font-size:11.5px;color:#a8252e}

.cp__ro{
    display:flex;gap:9px;align-items:flex-start;padding:11px 13px;
    background:#fff6e0;border-top:1px solid #fb8b03;font-size:12px;color:#6b4a00;line-height:1.5;
}
.cp__ro i{margin-top:2px;color:#fb8b03}
.cp__ro strong{display:block;color:#4d3500;margin-bottom:1px}

.cp__closed{border-top:1px solid #2451a8;background:#eef4ff;padding:11px 13px}
.cp__closedh{display:flex;align-items:flex-start;gap:8px}
.cp__closedh i{margin-top:2px;color:#2451a8}
.cp__closedh strong{font-size:12.5px;color:#1a2744;line-height:1.35}
.cp__closedb{margin:4px 0 0 23px;font-size:11.5px;color:#4a5562;line-height:1.45}
.cp__fake{
    margin:9px 0 0;padding:8px 9px;min-height:38px;background:#fff;
    border:1px dashed #dcdfe6;border-radius:4px;font-size:12.5px;color:#8b949e;
}

.cp__toast{
    position:fixed;right:20px;bottom:84px;z-index:1042;width:318px;max-width:calc(100vw - 40px);
    background:#fff;border:1px solid #dcdfe6;border-radius:6px;
    box-shadow:0 8px 26px rgba(26,39,68,.2);overflow:hidden;
}
.cp__toasth{
    display:flex;align-items:center;gap:7px;padding:9px 11px 0;
    font-size:10.5px;font-family:ui-monospace,Menlo,monospace;color:#6b7280;
}
.cp__toastw{color:#2451a8;font-weight:700}
.cp__toastcd{margin-left:auto}
.cp__toastb{display:flex;gap:9px;padding:7px 11px 10px}
.cp__toastb .cp__av{flex:0 0 26px;width:26px;height:26px}
.cp__toastn{font-size:12.5px;font-weight:700;color:#222}
.cp__toastx{font-size:12.5px;color:#4a5562;line-height:1.4;margin-top:1px}
.cp__toasta{display:flex;gap:7px;padding:0 11px 11px}
.cp__btn{
    padding:6px 12px;border-radius:4px;font-size:12px;font-weight:600;
    background:#fff;border:1px solid #dcdfe6;color:#4a5562;cursor:pointer;
}
.cp__btn:hover{border-color:#2451a8}
.cp__btn--p{background:#2451a8;border-color:#2451a8;color:#fff}
.cp__btn--p:hover{background:#1b3f85}
.cp__bar{height:3px;background:#e8ebf0}
.cp__bar i{display:block;height:100%;background:#fb8b03;transition:width 1s linear}

@media (max-width:560px){
  .cp__panel{width:100vw}
}

/* ── #2091 r4 — the pop-out shape ─────────────────────────────────────────
   Same component, filling its own window instead of hanging off the right of a
   page: no fixed positioning, no shadow, no border — the window frame is the
   edge now. */
.cp--window,
.cp--window .cp__panel{
    position:static;width:100%;height:100vh;max-width:none;
    border-left:0;box-shadow:none;
}
.cp--window .cp__panel{display:flex;flex-direction:column}

/* the "it is over there" pill left behind on the main page */
.cp__away{
    position:fixed;right:20px;bottom:20px;z-index:1040;
    display:flex;align-items:center;gap:10px;
    padding:10px 12px 10px 14px;
    background:#fff;border:1px solid #dcdfe6;border-left:3px solid #2451a8;border-radius:24px;
    box-shadow:0 5px 18px rgba(26,39,68,.18);
    font-size:12.5px;color:#4a5562;
}
.cp__away i{color:#2451a8}
.cp__awayb{
    padding:5px 12px;border-radius:14px;
    background:#2451a8;border:1px solid #2451a8;color:#fff;
    font-size:12px;font-weight:600;cursor:pointer;
}
.cp__awayb:hover{background:#1b3f85;border-color:#1b3f85}

.cp__blocked{
    display:flex;gap:8px;align-items:flex-start;
    padding:10px 13px;background:#fff6e0;border-bottom:1px solid #fb8b03;
    font-size:11.5px;line-height:1.45;color:#6b4a00;
}
.cp__blocked i{margin-top:2px;color:#fb8b03}
</style>
